// Base component (no leaf-theme override): the slideshow is pure DOM/CSSOM behaviour, so
// every theme inherits this runtime unchanged and supplies only CSS (and templates).
const component = require('../component/component');

// A single-slide slideshow fed over XHR. On init it asks its controller/action for the
// slides (a controller extending the core slideshow controller: `slides: [{html, clickUrl}]`, the html rendered on the
// server through the slideshow's slide template), wraps each in a slide element, inits the
// components they contain and moves through them.
//
// Looping without clones: the track always holds the slides in their logical order rotated
// so that the current slide comes first, at translateX(0). A move puts the target next to
// the current slide, animates the track by one slide width, then restores that rotated order
// starting at the target and resets the transform with no transition. Copies of the edge
// slides would duplicate the DOM ids of any component inside a slide (and hm.app only warns
// on a duplicate id before taking it over).
//
// The theme's track transition must apply only under [data-zit-sliding="true"] on the root:
// the reset after a move must not animate. With no transition at all (reduced motion), a
// move simply completes at once.
const slideshow = Object.assign({}, component, {
    notSubmittable: true,

    //request contract, read off the element by readZAttributes()
    controller: '',
    action: '',
    postParams: {},

    //behaviour
    interval: 6000,
    autoplay: true,
    showDots: true,
    showArrows: false,
    slideClass: '',
    dotClass: '',

    //Object.assign replaces the whole map, so the base's validationMessage is redeclared
    subelements: {
        validationMessage: null,
        viewport: null,
        track: null,
        loading: null,
        error: null,
        previousButton: null,
        nextButton: null,
        dotsHolder: null
    },

    slides: [],          // slide wrappers, in logical order
    current: 0,          // logical index of the slide on screen
    busy: false,         // a move is animating
    loading: false,
    hovered: false,
    focused: false,
    swiped: false,       // a swipe just moved the slides: swallow the click that may follow
    pointerStart: null,
    cancelMove: null,    // drops the pending move's listener and timer (set while a move animates)

    init: function () {
        component.init.call(this);

        if (!this.hasSubelement('track')) {
            return;
        }

        this.wire();
    },

    // Loading from inited() rather than init(): every component on the page is registered
    // before slides start injecting components of their own.
    inited: function () {
        component.inited.call(this);

        if (this.hasSubelement('track')) {
            this.load();
        }
    },

    // Public re-fetch. Reachable from an action configured with
    // 'method' => 'triggerLinkedComponent', 'controller' => '<slideshow html id>',
    // 'action' => 'refresh'.
    refresh: function () {
        return this.load();
    },

    load: async function () {
        if (this.loading) {
            return false;
        }

        if (!this.controller || !this.action) {
            this.hm.warn('slideshow without a controller/action', this.id, this.element);
            return false;
        }

        this.loading = true;
        this.showSub('loading');
        this.hideSub('error');

        let data = Object.assign({}, this.postParams, {
            slideshowConfigHash: this.configHash,
            csrf: this.hm.app.pageToken
        });

        let response = await this.hm.app.fetchJson(this.controller, this.action, data, 'POST');

        this.hideSub('loading');
        this.loading = false;

        if (response === false || !Array.isArray(response.slides)) {
            this.hm.warn('slideshow failed to load its slides', this.id, response);
            this.showSub('error');
            this.dispatch('zitSlidesLoadFailed', { response: response });
            return false;
        }

        this.build(response.slides);
        this.dispatch('zitSlidesLoaded', { count: this.slides.length, response: response });

        return true;
    },

    build: function (slides) {
        this.clear();

        slides.forEach((slide, index) => {
            let wrapper = document.createElement('div');

            if (this.slideClass) {
                wrapper.className = this.slideClass;
            }

            wrapper.dataset.zitSlideIndex = index;
            wrapper.append(...this.parseSlide(slide.html));

            if (slide.clickUrl) {
                // the whole slide is the link; its accessible name comes from its content
                wrapper.dataset.zitClickUrl = slide.clickUrl;
                wrapper.setAttribute('role', 'link');
                wrapper.tabIndex = 0;
            } else {
                wrapper.setAttribute('role', 'group');
                wrapper.setAttribute('aria-roledescription', this.hm.app.text('slide'));
                wrapper.setAttribute('aria-label', (index + 1) + ' / ' + slides.length);
            }

            this.subelements.track.appendChild(wrapper);
            this.slides.push(wrapper);
        });

        this.hm.app.initComponents(this.subelements.track);

        this.element.dataset.zitSlideCount = this.slides.length;

        // nothing to show: the slideshow takes no room at all
        this.hm.helper.showElement(this.element, this.slides.length > 0);

        let several = this.slides.length > 1;

        if (this.hasSubelement('previousButton')) {
            this.hm.helper.showElement(this.subelements.previousButton, several);
        }
        if (this.hasSubelement('nextButton')) {
            this.hm.helper.showElement(this.subelements.nextButton, several);
        }

        this.buildDots();
        this.updateState();
        this.scheduleNext();
    },

    // DOMParser never runs the scripts it parses; they are dropped so nobody expects them to run.
    parseSlide: function (html) {
        let body = new DOMParser().parseFromString(typeof html === 'string' ? html : '', 'text/html').body;

        body.querySelectorAll('script').forEach((script) => { script.remove(); });

        return Array.from(body.childNodes);
    },

    // Empty the track and deregister the components the slides brought in.
    clear: function () {
        this.clearTimer('slideshowAutoplay');

        // a move still animating would otherwise settle the new slides at its old index
        if (this.cancelMove !== null) {
            this.cancelMove();
        }

        this.removeSubcomponents();

        this.subelements.track.replaceChildren();
        this.subelements.track.style.transform = '';

        if (this.hasSubelement('dotsHolder')) {
            this.subelements.dotsHolder.replaceChildren();
        }

        this.slides = [];
        this.current = 0;
        this.busy = false;
        this.element.dataset.zitSliding = 'false';
    },

    // Called by hm.app.removeComponents() when the slideshow itself goes away, and by clear().
    removeSubcomponents: function () {
        let subComponentIds = this.hm.app.elementSubComponents(this.element).filter((componentId) => {
            return componentId !== this.id;
        });

        this.hm.app.removeComponents(subComponentIds);
    },

    buildDots: function () {
        if (!this.hasSubelement('dotsHolder')) {
            return;
        }

        this.subelements.dotsHolder.replaceChildren();

        let several = this.slides.length > 1;

        this.hm.helper.showElement(this.subelements.dotsHolder, several && this.showDots);

        if (!several) {
            return;
        }

        let goToSlide = this.hm.app.text('goToSlide');

        this.slides.forEach((slide, index) => {
            let dot = document.createElement('button');
            dot.type = 'button';

            if (this.dotClass) {
                dot.className = this.dotClass;
            }

            dot.dataset.zitDotIndex = index;
            dot.setAttribute('aria-label', goToSlide + ' ' + (index + 1));
            dot.addEventListener('click', () => { this.goTo(index); });

            this.subelements.dotsHolder.appendChild(dot);
        });
    },

    // Reflect the current slide: only it is reachable (the others are inert, so neither focus
    // nor assistive technology lands on a slide that is off screen), and its dot is active.
    updateState: function () {
        this.slides.forEach((slide, index) => {
            let isCurrent = index === this.current;

            slide.inert = !isCurrent;
            slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
        });

        if (this.hasSubelement('dotsHolder')) {
            this.subelements.dotsHolder.querySelectorAll('[data-zit-dot-index]').forEach((dot) => {
                let isCurrent = Number(dot.dataset.zitDotIndex) === this.current;

                dot.dataset.zitDotActive = isCurrent ? 'true' : 'false';
                dot.setAttribute('aria-current', isCurrent ? 'true' : 'false');
            });
        }
    },

    next: function () {
        this.goTo(this.current + 1, 1);
    },

    previous: function () {
        this.goTo(this.current - 1, -1);
    },

    // direction: 1 slides the target in from the right, -1 from the left; by default the
    // shorter way round is not sought — a dot further on moves forward, an earlier one back.
    goTo: function (index, direction) {
        let count = this.slides.length;

        if (count < 2 || this.busy) {
            return;
        }

        index = ((index % count) + count) % count;

        if (index === this.current) {
            return;
        }

        if (typeof direction === typeof undefined) {
            direction = index > this.current ? 1 : -1;
        }

        this.busy = true;
        this.clearTimer('slideshowAutoplay');

        let track = this.subelements.track;
        let target = this.slides[index];
        let currentSlide = this.slides[this.current];

        target.inert = false;

        if (direction > 0) {
            track.insertBefore(target, currentSlide.nextSibling);
            this.animate('-100%', () => { this.settle(index); });
        } else {
            // the target goes in front, and the track jumps one slide left so the current
            // slide stays on screen, then slides back to 0
            track.insertBefore(target, currentSlide);
            track.style.transform = 'translateX(-100%)';
            void track.offsetWidth;
            this.animate('0%', () => { this.settle(index); });
        }
    },

    animate: function (position, done) {
        let track = this.subelements.track;
        let finished = false;

        let cancel = () => {
            finished = true;
            track.removeEventListener('transitionend', onEnd);
            this.clearTimer('slideshowMove');
            this.cancelMove = null;
        };

        let finish = () => {
            if (finished) {
                return;
            }

            cancel();
            done();
        };

        let onEnd = (e) => {
            if (e.target === track && e.propertyName === 'transform') {
                finish();
            }
        };

        this.element.dataset.zitSliding = 'true';
        track.style.transform = 'translateX(' + position + ')';

        let duration = this.transitionTime(track);

        if (duration === 0) {
            finish();
            return;
        }

        track.addEventListener('transitionend', onEnd);
        this.cancelMove = cancel;
        // transitionend does not fire when the transition is cancelled (e.g. the element got
        // hidden), so the move is completed anyway shortly after it should have ended
        this.startTimer('slideshowMove', finish, duration + 100);
    },

    // The longest transform transition the theme set on the track, in ms (0 = none).
    transitionTime: function (element) {
        let style = getComputedStyle(element);
        let toMs = (value) => {
            value = value.trim();
            return value.endsWith('ms') ? parseFloat(value) : parseFloat(value) * 1000;
        };

        let durations = style.transitionDuration.split(',').map(toMs);
        let delays = style.transitionDelay.split(',').map(toMs);
        let longest = 0;

        durations.forEach((duration, index) => {
            let total = (duration || 0) + (delays[index % delays.length] || 0);
            longest = Math.max(longest, total);
        });

        return longest;
    },

    settle: function (index) {
        let track = this.subelements.track;
        let count = this.slides.length;
        let hadFocus = track.contains(document.activeElement);

        this.element.dataset.zitSliding = 'false';
        this.current = index;

        for (let i = 0; i < count; i++) {
            track.appendChild(this.slides[(index + i) % count]);
        }

        track.style.transform = 'translateX(0)';

        this.updateState();

        // moving a focused slide drops its focus; hand it to the slide now on screen
        if (hadFocus && this.slides[index].tabIndex >= 0) {
            this.slides[index].focus({ preventScroll: true });
        }

        this.busy = false;
        this.scheduleNext();
    },

    autoplaying: function () {
        return this.autoplay
            && this.slides.length > 1
            && !this.hovered
            && !this.focused
            && !document.hidden
            && !window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    },

    scheduleNext: function () {
        this.clearTimer('slideshowAutoplay');

        if (this.autoplaying()) {
            this.startTimer('slideshowAutoplay', () => { this.next(); }, Math.max(1000, this.interval));
        }
    },

    wire: function () {
        if (this.hasSubelement('previousButton')) {
            this.subelements.previousButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.previous();
            });
        }

        if (this.hasSubelement('nextButton')) {
            this.subelements.nextButton.addEventListener('click', (e) => {
                e.preventDefault();
                this.next();
            });
        }

        // autoplay holds while the pointer is over the slideshow or the keyboard focus is
        // inside it, and while the page is not visible. A dot clicked with the mouse takes
        // the focus too, but not :focus-visible, so it does not stop autoplay for good.
        this.element.addEventListener('mouseenter', () => {
            this.hovered = true;
            this.scheduleNext();
        });

        this.element.addEventListener('mouseleave', () => {
            this.hovered = false;
            this.scheduleNext();
        });

        this.element.addEventListener('focusin', (e) => {
            this.focused = e.target.matches(':focus-visible');
            this.scheduleNext();
        });

        this.element.addEventListener('focusout', (e) => {
            if (e.relatedTarget === null || !this.element.contains(e.relatedTarget)) {
                this.focused = false;
                this.scheduleNext();
            }
        });

        document.addEventListener('visibilitychange', () => { this.scheduleNext(); });

        this.element.addEventListener('keydown', (e) => {
            if (e.key === 'ArrowLeft') {
                e.preventDefault();
                this.previous();
            } else if (e.key === 'ArrowRight') {
                e.preventDefault();
                this.next();
            } else if (e.key === 'Enter') {
                let slide = e.target.closest('[data-zit-click-url]');

                if (slide !== null && slide === e.target) {
                    e.preventDefault();
                    this.open(slide.dataset.zitClickUrl, e);
                }
            }
        });

        this.subelements.track.addEventListener('click', (e) => {
            if (this.swiped) {
                this.swiped = false;
                return;
            }

            let slide = e.target.closest('[data-zit-click-url]');

            // a link or a control inside the slide does its own thing
            if (slide === null || e.target.closest('a, button, input, select, textarea, label') !== null) {
                return;
            }

            e.preventDefault();
            this.open(slide.dataset.zitClickUrl, e);
        });

        // touch swipe; the theme sets touch-action: pan-y on the viewport so vertical
        // scrolling still belongs to the page
        let viewport = this.hasSubelement('viewport') ? this.subelements.viewport : this.element;

        viewport.addEventListener('pointerdown', (e) => {
            this.swiped = false;
            this.pointerStart = (e.pointerType === 'mouse') ? null : { x: e.clientX, y: e.clientY };
        });

        viewport.addEventListener('pointerup', (e) => {
            if (this.pointerStart === null) {
                return;
            }

            let dx = e.clientX - this.pointerStart.x;
            let dy = e.clientY - this.pointerStart.y;

            this.pointerStart = null;

            if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) {
                this.swiped = true;

                if (dx < 0) {
                    this.next();
                } else {
                    this.previous();
                }
            }
        });

        viewport.addEventListener('pointercancel', () => { this.pointerStart = null; });
    },

    open: function (url, e) {
        if (e.ctrlKey || e.metaKey || e.shiftKey) {
            window.open(url, '_blank', 'noopener');
        } else {
            window.location.assign(url);
        }
    },

    dispatch: function (eventName, detail) {
        this.element.dispatchEvent(new CustomEvent(eventName, {
            bubbles: true,
            detail: Object.assign({ slideshow: this }, detail)
        }));
    },

    getValue: function () {
        return null;
    },

    formPostData: function (formPostData) {
        return formPostData;
    }
});

module.exports = slideshow;
